'use client';

import classNames from 'classnames';
import { useEffect, useId, useState } from 'react';
import { CmsOffer, CmsProduct } from '../../types/cmsProduct';
import {
  getDaysUntil,
  getEffectiveGiftPrice,
} from '../../utils/helpers/calculateRewardValue';
import {
  filterActiveOffers,
  getChooseOneOffers,
  getEarliestEndingOffer,
} from '../../utils/helpers/offers';
import { tr } from '../../utils/translations/tr';
import { MaterialIcon } from '../MaterialIcon';
import {
  CcProductCardGiftList,
  CcProductCardGiftOption,
} from './CcProductCardGiftList';
import { CcProductCardRewardRow } from './CcProductCardRewardRow';
import { CcProductCardUrgencyPill } from './CcProductCardUrgencyPill';
import { CcProductCardTranslations } from './i18n';
import {
  CC_CARD_TEXT,
  formatCriterion,
  formatGiftValue,
  formatMarketDate,
  formatTopUp,
  giftTitle,
} from './utils';

type CcProductCardRewardsProps = {
  product: CmsProduct;
  t: CcProductCardTranslations;
  currency: string;
  lang: string;
  /** The reward options were opened or closed: the new state, for analytics. */
  onToggle?: (expanded: boolean) => void;
};

const MAX_CONDITIONS = 3;
// FR-1.5.8: a pill within 30 days, escalating within 7.
const URGENCY_DAYS = 30;
const URGENT_DAYS = 7;

/**
 * The rewards block (BRD-01 FR-1.5): the MoneyHero exclusive offer leads with its highest-value
 * gift, the rest open in place; the bank's welcome offer follows as its own, secondary line. The two
 * are never summed into one figure (FR-1.5.10).
 */
export const CcProductCardRewards: React.FC<CcProductCardRewardsProps> = ({
  product,
  t,
  currency,
  lang,
  onToggle,
}) => {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  const now = useMountedNow();

  // As on the PDP: once the reader's clock is known, an offer that has ended drops out. A cached page
  // can still carry one for a while after it ends.
  const keepActive = (offers: CmsOffer[] = []) =>
    now !== undefined ? filterActiveOffers(offers, new Date(now)) : offers;
  const moneyHeroOffers = keepActive(getChooseOneOffers(product));
  const welcomeOffers = keepActive(product.welcome_offers);
  if (!moneyHeroOffers.length && !welcomeOffers.length) return null;

  // Highest value first, whatever order the editor entered them in (FR-1.5.3); a stable sort keeps
  // the authored order among equals.
  const options = moneyHeroOffers
    .flatMap((offer) =>
      (offer.list_of_gifts ?? []).map((item) => ({ offer, item })),
    )
    .filter(({ item }) => item.gift?.gift_title?.trim())
    // Net of a top-up: the value the Sort and Filter panel ranks gifts by.
    .map((option) => ({
      ...option,
      value: getEffectiveGiftPrice(option.item.gift),
    }))
    .sort((a, b) => b.value - a.value);
  const top = options[0];
  const giftOption = (
    { item, value }: (typeof options)[number],
    index: number,
  ): CcProductCardGiftOption => ({
    id: `${item.id}-${index}`,
    title: giftTitle(item),
    value: formatGiftValue(item, currency),
    topUp: formatTopUp(item.gift, currency, t.cc_card_top_up),
    isHighest: index === 0 && value > 0,
  });

  const conditions = (top?.item.criteria_list ?? [])
    .map(formatCriterion)
    .filter((condition): condition is string => !!condition)
    .slice(0, MAX_CONDITIONS);
  // The end date, like the conditions, belongs to the offer of the gift on show: another MoneyHero
  // offer ending sooner must not put "2 days left" beside it. With no gift on show (or no date on its
  // offer), the offer that ends first.
  const promoEndDate =
    top?.offer.period?.end_date ??
    getEarliestEndingOffer(moneyHeroOffers)?.period?.end_date;
  const promoEnds = promoEndDate
    ? tr('cc_card_promo_ends', t, {
        date: formatMarketDate(promoEndDate, lang),
      })
    : undefined;
  // Counted against the reader's clock after mount, so the server and the hydration render agree;
  // the same count as the PDP's urgency phase.
  const daysLeft =
    promoEndDate && now !== undefined
      ? (getDaysUntil(promoEndDate, now) ?? null)
      : null;

  const moreCount = options.length - 1;
  const details = (
    <CcProductCardGiftList
      id={listId}
      options={moreCount > 0 ? options.map(giftOption) : []}
      highestLabel={t.cc_card_highest_value}
      conditions={conditions}
      promoEnds={promoEnds}
    />
  );

  const bankGifts = welcomeOffers.flatMap((offer) => offer.list_of_gifts ?? []);
  const topBankGift = [...bankGifts].sort(
    (a, b) => getEffectiveGiftPrice(b.gift) - getEffectiveGiftPrice(a.gift),
  )[0];

  const moneyHeroRow = (
    <CcProductCardRewardRow
      tone="moneyhero"
      icon="card_giftcard"
      image={
        top?.item.gift?.desktop_image ||
        top?.item.gift?.mobile_image ||
        undefined
      }
      eyebrow={t.cc_card_moneyhero_exclusive}
      eyebrowAside={
        daysLeft !== null && daysLeft <= URGENCY_DAYS ? (
          <CcProductCardUrgencyPill
            label={
              // Under a day left counts as 1; an ended offer has already dropped out above.
              daysLeft <= 1
                ? t.cc_card_last_day
                : tr(
                    'cc_card_days_left',
                    t,
                    { days: String(daysLeft) },
                    daysLeft,
                  )
            }
            urgent={daysLeft <= URGENT_DAYS}
            description={promoEnds ?? ''}
          />
        ) : undefined
      }
      title={top ? giftTitle(top.item) : moneyHeroOffers[0]?.offer_title}
      note={
        top ? formatTopUp(top.item.gift, currency, t.cc_card_top_up) : undefined
      }
      value={top ? formatGiftValue(top.item, currency) : undefined}
      trailing={
        moreCount > 0 && (
          <MaterialIcon
            icon={expanded ? 'expand_less' : 'expand_more'}
            px={16}
            className="shrink-0 text-text/secondary-lighter"
          />
        )
      }
    >
      {moreCount > 0 && (
        <span
          className={classNames(
            CC_CARD_TEXT.note,
            'text-text/secondary-lighter',
          )}
        >
          {expanded
            ? t.cc_card_fewer_rewards
            : tr(
                'cc_card_more_rewards',
                t,
                { count: String(moreCount) },
                moreCount,
              )}
        </span>
      )}
    </CcProductCardRewardRow>
  );

  return (
    <section
      data-component="cc-product-card-rewards"
      className="flex flex-col gap-spacing/gap/soft-12px"
    >
      {moneyHeroOffers.length > 0 && (
        <div className="rounded-radius/card-inner bg-highlights/orange-light">
          {/* With more options the whole row opens them, as one 44px+ target. */}
          {moreCount > 0 ? (
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls={listId}
              onClick={() => {
                setExpanded(!expanded);
                onToggle?.(!expanded);
              }}
              className="w-full px-3.5 py-2.5 text-left"
            >
              {moneyHeroRow}
            </button>
          ) : (
            <div className="px-3.5 py-2.5">{moneyHeroRow}</div>
          )}
          {/* With one option there is nothing to open: its conditions and end date show as they are.
              With more, the list stays mounted (hidden) so the toggle always controls an element. */}
          {(moreCount > 0 || conditions.length > 0 || !!promoEnds) && (
            <div
              hidden={moreCount > 0 && !expanded}
              className="px-3.5 pb-spacing/padding/soft-12px"
            >
              {details}
            </div>
          )}
        </div>
      )}
      {welcomeOffers.length > 0 && (
        <div className="rounded-radius/card-inner px-3.5 py-spacing/padding/soft-8px bg-fill/primary-light">
          <CcProductCardRewardRow
            tone="bank"
            icon="account_balance"
            eyebrow={t.cc_card_bank_offer}
            // A welcome offer without a named gift still says what it is (FR-1.8.5).
            title={
              (topBankGift && giftTitle(topBankGift)) ||
              welcomeOffers.find((offer) => offer.offer_title?.trim())
                ?.offer_title
            }
            // The value is net of the top-up, so the top-up shows beneath it, as on the MoneyHero row.
            note={formatTopUp(topBankGift?.gift, currency, t.cc_card_top_up)}
            value={topBankGift && formatGiftValue(topBankGift, currency)}
          />
        </div>
      )}
    </section>
  );
};

function useMountedNow(): number | undefined {
  const [now, setNow] = useState<number>();
  useEffect(() => setNow(Date.now()), []);
  return now;
}
